import { matrixToPermissionStrings } from '@evi/shared';

/**
 * The Driver column on every Admin session list (Warren, item 9,
 * 2026-10-23): "every session row (failed ones included) shows the
 * driver's name, linked to their driver profile. The driver profile lists
 * their sessions with links back."
 *
 * The Api decides what a row may say: `driver` (apps/api
 * lib/sessionDriverRef.ts) reaches only a caller who can open the driver
 * profile, with profile_id set only when that profile opens for them. A
 * caller without it keeps today's driver_name (a Site's reads 'Driver').
 * The link is also gated here on the live permission string the profile
 * route checks (User:read), the nav's own rule for the Drivers leaf, so a
 * stale row never offers a page that would 403.
 */

/** The profile route's permission string (GET /admin/drivers/:id gates on
 *  User:read), derived through the shared translator like the nav's. */
export const DRIVER_PROFILE_PERMISSION: string = matrixToPermissionStrings([
  { resource: 'User', create: false, read: true, update: false, delete: false },
])[0] ?? 'user:read';

export function canOpenDriverProfiles(
  user: { role?: string | null; permissions?: readonly string[] | null } | null | undefined,
): boolean {
  if (!user) return false;
  // super_admin passes by bypass, as requireMatrixPermission does.
  if (user.role === 'super_admin') return true;
  return (user.permissions ?? []).includes(DRIVER_PROFILE_PERMISSION);
}

export function driverProfilePath(driverId: string): string {
  return `/drivers/${driverId}`;
}

export function sessionDetailPath(sessionId: string): string {
  return `/sessions/${sessionId}`;
}

export type SessionDriverKind = 'driver' | 'fleet' | 'rfid' | 'vehicle' | 'commissioning' | 'none';

/** The fields of a session row (any list) this reads. */
export interface DriverCellRow {
  driver?: { kind: SessionDriverKind; label: string; profile_id: string | null } | null;
  driver_name?: string | null;
  rfid_tag?: string | null;
  credential_kind?: 'rfid' | 'vid' | null;
}

export interface DriverDisplay {
  /** What the cell says. Never blank. */
  text: string;
  /** Where the name links, or null for plain text. */
  href: string | null;
  /** Muted styling for a row nobody is recorded against. */
  muted: boolean;
}

/**
 * One row's Driver cell. With the Api's `driver`: its label, linked when
 * the profile opens and the viewer holds the permission. Without it (a
 * caller the Api tells less): today's driver_name, else what the row's
 * own credential says, else that nothing was recorded.
 */
export function sessionDriverDisplay(row: DriverCellRow, canOpenProfiles: boolean): DriverDisplay {
  const ref = row.driver;
  if (ref && typeof ref.label === 'string' && ref.label.trim()) {
    const href = canOpenProfiles && ref.kind === 'driver' && ref.profile_id ? driverProfilePath(ref.profile_id) : null;
    return { text: ref.label.trim(), href, muted: ref.kind === 'none' };
  }
  const name = typeof row.driver_name === 'string' ? row.driver_name.trim() : '';
  if (name) return { text: name, href: null, muted: false };
  if (typeof row.rfid_tag === 'string' && row.rfid_tag.length > 0) {
    return { text: row.credential_kind === 'vid' ? 'Autocharge vehicle' : 'RFID card', href: null, muted: false };
  }
  return { text: 'No driver recorded', href: null, muted: true };
}

/** Status labels for the session lists this change adds. Every status the
 *  Session enum carries, pending and sweeping included (a start that never
 *  reached the charger reads Pending, then Failed). */
export const SESSION_STATUS_LABELS: Readonly<Record<string, string>> = {
  pending: 'Pending',
  sweeping: 'Pending',
  active: 'Active',
  completed: 'Completed',
  failed: 'Failed',
  stopped_by_user: 'Stopped (User)',
  stopped_by_system: 'Stopped (System)',
};

export function sessionStatusLabel(status: string): string {
  return SESSION_STATUS_LABELS[status] ?? status;
}

/** The status filter the charger and site session lists offer. Values are
 *  GET /sessions ?status values (exact statuses). */
export const SESSION_LIST_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'stopped_by_user', label: 'Stopped by user' },
  { value: 'stopped_by_system', label: 'Stopped by system' },
];

export type DriverSessionWindow = 'all' | 'last_7d' | 'last_30d' | 'last_90d';

/** The driver profile's date filter as GET /admin/drivers/:id/sessions
 *  ?from, or undefined for any date. */
export function driverSessionsFrom(window: DriverSessionWindow, now: Date = new Date()): string | undefined {
  const days = window === 'last_7d' ? 7 : window === 'last_30d' ? 30 : window === 'last_90d' ? 90 : 0;
  if (days === 0) return undefined;
  return new Date(now.getTime() - days * 86_400_000).toISOString();
}

/** A click that should open a new tab or window keeps the browser's own
 *  behaviour; every other click navigates in place. */
export function isPlainLeftClick(e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}
